import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingsAPI, labelsAPI, teamsAPI } from '../services/api';
import ActiveTimerWidget from '../components/ActiveTimerWidget';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  LayoutDashboard,
  CheckSquare,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Package,
  ShieldCheck,
  Bell,
  Sparkles,
  Tag,
  UserPlus,
  Check,
  Clock,
  Receipt
} from 'lucide-react';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [dismissedNotifications, setDismissedNotifications] = useState(() => {
    const saved = localStorage.getItem('dismissedNotifications');
    return saved ? JSON.parse(saved) : [];
  });

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/dashboard/tasks', icon: CheckSquare, label: 'Tasks' },
    { to: '/dashboard/teams', icon: Users, label: 'Teams' },
    { to: '/dashboard/orders', icon: Package, label: 'Parts Orders' },
    { to: '/dashboard/quotes', icon: Receipt, label: 'Quotes' },
    { to: '/dashboard/time-reports', icon: Clock, label: 'Time Reports' },
    { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
  ];

  if (user?.role === 'admin') {
    navItems.push({ to: '/dashboard/admin', icon: ShieldCheck, label: 'Admin' });
  }

  // Check for notification conditions
  useEffect(() => {
    const checkNotifications = async () => {
      if (!user) return;
      
      const newNotifications = [];
      
      try {
        // Check AI domain setting
        const settingsRes = await settingsAPI.get();
        const defaultPrompt = "You are a helpful project management assistant.";
        if (!settingsRes.data.domain_prompt || settingsRes.data.domain_prompt === defaultPrompt) {
          newNotifications.push({
            id: 'ai-domain',
            type: 'ai',
            icon: Sparkles,
            title: 'Configure AI Assistant',
            description: 'Customize your AI domain prompt for better task suggestions.',
            action: () => navigate('/dashboard/settings'),
            actionLabel: 'Go to Settings'
          });
        }
        
        // Check labels
        const labelsRes = await labelsAPI.list();
        if (labelsRes.data.length === 0) {
          newNotifications.push({
            id: 'no-labels',
            type: 'labels',
            icon: Tag,
            title: 'Create Labels',
            description: 'Add labels to categorize and organize your tasks.',
            action: () => navigate('/dashboard/settings'),
            actionLabel: 'Create Labels'
          });
        }
        
        // Check group invitations
        const groupsRes = await teamsAPI.list();
        const hasInvites = groupsRes.data.some(g => g.pending_invites?.length > 0);
        const hasMultipleMembers = groupsRes.data.some(g => g.members?.length > 1);
        if (groupsRes.data.length > 0 && !hasInvites && !hasMultipleMembers) {
          newNotifications.push({
            id: 'invite-members',
            type: 'invite',
            icon: UserPlus,
            title: 'Invite Team Members',
            description: 'Collaborate with others by inviting them to your teams.',
            action: () => navigate('/dashboard/teams'),
            actionLabel: 'Invite Members'
          });
        }
        
        // Filter out dismissed notifications
        const filtered = newNotifications.filter(n => !dismissedNotifications.includes(n.id));
        setNotifications(filtered);
      } catch (error) {
        console.error('Error checking notifications:', error);
      }
    };
    
    checkNotifications();
  }, [user, dismissedNotifications, navigate]);

  const dismissNotification = (id) => {
    const updated = [...dismissedNotifications, id];
    setDismissedNotifications(updated);
    localStorage.setItem('dismissedNotifications', JSON.stringify(updated));
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 sidebar-dark border-r border-slate-800 fixed h-full">
        <div className="p-6">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl font-heading">T</span>
            </div>
            <span className="text-white font-heading font-bold text-xl">TaskMaster</span>
          </div>
        </div>

        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-item flex items-center space-x-3 px-4 py-3 ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center space-x-3 w-full px-4 py-2 rounded-md hover:bg-slate-800 transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-indigo-600 text-white text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-64 sidebar-dark animate-slide-in">
            <div className="p-6 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">T</span>
                </div>
                <span className="text-white font-bold text-xl">TaskMaster</span>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="text-slate-400 hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>

            <nav className="px-4 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-item flex items-center space-x-3 px-4 py-3 ${isActive ? 'active' : ''}`
                  }
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:ml-64 flex-1 flex flex-col min-h-screen">
        {/* Top Bar - Desktop */}
        <header className="hidden lg:flex sticky top-0 z-40 glass border-b border-slate-200 px-6 py-3 justify-end items-center gap-3">
          <ActiveTimerWidget />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="relative">
                <Bell className="h-5 w-5" />
                {notifications.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500">
                    {notifications.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Notifications</h4>
                {notifications.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">All caught up!</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div key={notification.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <notification.icon className="h-4 w-4 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{notification.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{notification.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Button size="sm" variant="default" className="h-7 text-xs" onClick={notification.action}>
                                {notification.actionLabel}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 text-xs"
                                onClick={() => dismissNotification(notification.id)}
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Dismiss
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </header>
        
        {/* Top Bar - Mobile */}
        <header className="lg:hidden sticky top-0 z-40 glass border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="text-slate-600">
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <span className="font-bold text-slate-900">TaskMaster</span>
            </div>
            <div className="flex items-center gap-2">
              <ActiveTimerWidget compact />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="relative h-8 w-8 p-0">
                    <Bell className="h-5 w-5" />
                    {notifications.length > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-xs bg-red-500">
                        {notifications.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Notifications</h4>
                    {notifications.length === 0 ? (
                      <p className="text-sm text-slate-500 py-4 text-center">All caught up!</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {notifications.map((notification) => (
                          <div key={notification.id} className="p-2 bg-slate-50 rounded-lg">
                            <div className="flex items-start gap-2">
                              <notification.icon className="h-4 w-4 text-indigo-600 mt-0.5" />
                              <div className="flex-1">
                                <p className="font-medium text-xs">{notification.title}</p>
                                <div className="flex items-center gap-1 mt-1">
                                  <Button size="sm" variant="default" className="h-6 text-xs px-2" onClick={notification.action}>
                                    {notification.actionLabel}
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 text-xs px-2"
                                    onClick={() => dismissNotification(notification.id)}
                                  >
                                    Dismiss
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-indigo-600 text-white text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
